/*
 * Copyright (c) 2021.
 * 2iPartners SàRL and/or its affiliates.
 * All rights reserved.
 *
 * 2iPartners SàRL
 * Route de Compois 24,
 * 1252 Meinier
 * Suisse
 *
 * tel : +41 22 735 97 07
 */

import {Autopsy} from '../_models/autopsy';
import {MatDialog} from '@angular/material/dialog';

import {Injectable} from '@angular/core';
import {
  DialogConfirmAutopsyComponent
} from '../_shared/dialogs/dialog-confirm-autopsy/dialog-confirm-autopsy.component';
import {ExternalImportRemoval} from '../_models/external-import-removal';
import {Observable} from 'rxjs';
import {RemovalImportType} from '../_forms/removal-import-type';
import {Removal} from '../_models/removal';
import {
  DialogRemovalFinishedComponent
} from '../_shared/dialogs/dialog-removal-finished/dialog-removal-finished.component';
import {
  DialogRemovalValidateComponent
} from '../_shared/dialogs/dialog-removal-validate/dialog-removal-validate.component';
import {
  DialogThirdPartyLockedComponent
} from '../_shared/dialogs/dialog-third-party-locked/dialog-third-party-locked.component';
import {DialogAngeeComponent} from '../_shared/dialogs/dialog-angee/dialog-angee.component';
import {RemovalPoint} from '../_models/removal-point';
import {
  DialogRemovalPointDisableComponent
} from '../_shared/dialogs/dialog-removal-point-disable/dialog-removal-point-disable.component';
import {
  DialogRemovalPointEnableComponent
} from '../_shared/dialogs/dialog-removal-point-enable/dialog-removal-point-enable.component';
import {
  DialogRemovalPointFavoriteComponent
} from '../_shared/dialogs/dialog-removal-point-favorite/dialog-removal-point-favorite.component';
import {
  DialogRemovalPointFormComponent,
  DialogRemovalPointFormData
} from '../_shared/dialogs/dialog-removal-point-form/dialog-removal-point-form.component';
import {
  DialogBoviclicCancelComponent
} from '../_shared/dialogs/dialog-boviclic-cancel/dialog-boviclic-cancel.component';
import {DialogAtmInvalidComponent} from '../_shared/dialogs/dialog-atm-invalid/dialog-atm-invalid.component';
import {
  DialogNotAllowedSiteComponent
} from '../_shared/dialogs/dialog-not-allowed-site/dialog-not-allowed-site.component';
import {DialogUserEmailUsedComponent} from '../_shared/dialogs/dialog-user-email-used/dialog-user-email-used.component';
import {BoviclicAlertComponent} from "../dialog/boviclic-alert/boviclic-alert.component";

enum DialogWidth {
  ANGEE = '800px',
  ATM_INVALID = '500px',
  NOT_ALLOWED_SITE = '500px',
  BOVICLIC_CANCEL = '500px',
  CONFIRM_AUTOPSY = '400px',
  REMOVAL_FINISHED = '700px',
  REMOVAL_POINT_DISABLE = '500px',
  REMOVAL_POINT_ENABLE = '500px',
  REMOVAL_POINT_FAVORITE = '500px',
  REMOVAL_POINT_FORM = '650px',
  REMOVAL_VALIDATE = '900px',
  THIRD_PARTY_LOCKED = '500px',
  USER_EMAIL_ALREADY_USED = '500px',
}

@Injectable()
export class DialogHelper {

  constructor(public dialog: MatDialog) {
  }

  public showAngee(): Observable<any> {
    const dialogRef = this.dialog.open(DialogAngeeComponent, {
      autoFocus: true,
      disableClose: true,
      width: DialogWidth.ANGEE
    });

    return dialogRef.afterClosed();
  }

  public showAtmInvalid(msg: string): Observable<any> {
    const dialogRef = this.dialog.open(DialogAtmInvalidComponent, {
      autoFocus: true,
      data: msg,
      disableClose: true,
      width: DialogWidth.ATM_INVALID
    });

    return dialogRef.afterClosed();
  }

  public showBoviclicCancel(): Observable<any> {
    const dialogRef = this.dialog.open(DialogBoviclicCancelComponent, {
      autoFocus: true,
      disableClose: true,
      width: DialogWidth.BOVICLIC_CANCEL
    });

    return dialogRef.afterClosed();
  }

  public showConfirmAutopsy(autopsy: Autopsy): Observable<any> {
    const dialogRef = this.dialog.open(DialogConfirmAutopsyComponent, {
      autoFocus: true,
      data: autopsy,
      disableClose: true,
      width: DialogWidth.CONFIRM_AUTOPSY
    });

    return dialogRef.afterClosed();
  }

  public showNotAllowedSite(msg: string): Observable<any> {
    const dialogRef = this.dialog.open(DialogNotAllowedSiteComponent, {
      autoFocus: true,
      data: msg,
      disableClose: true,
      width: DialogWidth.NOT_ALLOWED_SITE
    });

    return dialogRef.afterClosed();
  }

  public showRemovalFinished(removal: Removal): Observable<any> {
    const dialogRef = this.dialog.open(DialogRemovalFinishedComponent, {
      autoFocus: true,
      data: removal,
      disableClose: true,
      width: DialogWidth.REMOVAL_FINISHED
    });

    return dialogRef.afterClosed();
  }

  public showRemovalPointDisable(removalPoint: RemovalPoint): Observable<any> {
    const dialogRef = this.dialog.open(DialogRemovalPointDisableComponent, {
      autoFocus: true,
      data: removalPoint,
      disableClose: true,
      width: DialogWidth.REMOVAL_POINT_DISABLE
    });

    return dialogRef.afterClosed();
  }

  public showRemovalPointEnable(removalPoint: RemovalPoint): Observable<any> {
    const dialogRef = this.dialog.open(DialogRemovalPointEnableComponent, {
      autoFocus: true,
      data: removalPoint,
      disableClose: true,
      width: DialogWidth.REMOVAL_POINT_ENABLE
    });

    return dialogRef.afterClosed();
  }

  public showRemovalPointForm(data: DialogRemovalPointFormData): Observable<any> {
    const dialogRef = this.dialog.open(DialogRemovalPointFormComponent, {
      autoFocus: true,
      data: data,
      disableClose: true,
      width: DialogWidth.REMOVAL_POINT_FORM
    });

    return dialogRef.afterClosed();
  }

  public showRemovalPointFavorite(removalPoint: RemovalPoint): Observable<any> {
    const dialogRef = this.dialog.open(DialogRemovalPointFavoriteComponent, {
      autoFocus: true,
      data: removalPoint,
      disableClose: true,
      width: DialogWidth.REMOVAL_POINT_FAVORITE
    });

    return dialogRef.afterClosed();
  }

  public showRemovalValidate(submittedRemoval: RemovalImportType, externalRemoval: ExternalImportRemoval): Observable<any> {
    const dialogRef = this.dialog.open(DialogRemovalValidateComponent, {
      autoFocus: true,
      data: {
        externalRemoval: externalRemoval,
        submittedRemoval: submittedRemoval
      },
      disableClose: true,
      width: DialogWidth.REMOVAL_VALIDATE
    });

    return dialogRef.afterClosed();
  }

  public showThirdPartyLocked() {
    const dialogRef = this.dialog.open(DialogThirdPartyLockedComponent, {
      autoFocus: true,
      disableClose: true,
      width: DialogWidth.THIRD_PARTY_LOCKED
    });

    return dialogRef.afterClosed();
  }

  public showUserEmailAlreadyUsed() {
    const dialogRef = this.dialog.open(DialogUserEmailUsedComponent, {
      autoFocus: true,
      disableClose: true,
      width: DialogWidth.USER_EMAIL_ALREADY_USED
    });

    return dialogRef.afterClosed();
  }

  public showExternalRemovalRequest(data : {message: string, showContact: boolean, referer?: string}) {
    this.dialog.open(BoviclicAlertComponent, {
      autoFocus: true,
      disableClose: true,
      width: '90vw',
      height: '90vh',
      maxWidth: '90vw',
      data: data
    })
  }

}
